import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";

// General
import ForgotPassword from "@/views/ForgotPassword.vue";
import Help from "@/views/Help.vue";
import Home from "@/views/Home.vue";
import Login from "@/views/Login.vue";
import Profile from "@/views/Profile.vue";
import SignUp from "@/views/SignUp.vue";

// BizDevOps
import Biz from "@/views/Biz.vue";
import Dev from "@/views/Dev.vue";
import Ops from "@/views/Ops.vue";

// Utility pages
import NotFoundComponent from "@/views/NotFound.vue";
import RedirectByIdPage from "@/views/RedirectByIdPage.vue";
import RedirectByNamePage from "@/views/RedirectByNamePage.vue";

// BizDevOps subitems
import Application from "@/views/Application.vue";
import Dashboard from "@/views/Dashboard.vue";
import Domain from "@/views/Domain.vue";
import DomainPage from "@/views/DomainPage.vue";
import Process from "@/views/Process.vue";
import Product from "@/views/Product.vue";
import Service from "@/views/Service.vue";
import UserManagement from "@/views/UserManagement.vue";

// Instances
import BusinessEntityPage from "@/views/BusinessEntityPage.vue";
import DocumentPage from "@/views/DocumentPage.vue";
import OrgInst from "@/views/OrgInst.vue";
import ProcessDefinitionPage from "@/views/ProcessDefinitionPage.vue";
import ProcessElementPage from "@/views/ProcessElementPage.vue";
import ProcessPage from "@/views/ProcessPage.vue";
import ProductPage from "@/views/ProductPage.vue";
import RolePage from "@/views/RolePage.vue";
import StekPage from "@/views/StekPage.vue";

// Create
import CreateStek from "@/views/CreateStek.vue";

// Live
import Live from "@/live/views/Live.vue";
//
// Dev only
import AutoLogin from "@/views/AutoLogin.vue";
import CodePreviewDemo from "@/views/CodePreviewDemo.vue";

import type * as Layouts from "@/layouts";
import mixpanel from "mixpanel-browser";

declare module "vue-router" {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresOrg?: boolean;
        requiresStek?: boolean;
        breadcrumbs?: string[] | "auto";
        layout?: keyof typeof Layouts;
        mode?: "design" | "live";
    }
}

const baseRoutes: Array<RouteRecordRaw> = [
    {
        path: "/home",
        name: "home",
        component: Home,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/login",
        name: "login",
        component: Login,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/signup",
        name: "signup",
        component: SignUp,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/forgot-password",
        name: "forgotPassword",
        component: ForgotPassword,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/biz",
        name: "biz",
        component: Biz,
    },
    {
        path: "/ops",
        name: "ops",
        component: Ops,
    },
    {
        path: "/domain",
        name: "domain",
        component: Domain,
    },
    {
        path: "/product",
        name: "product",
        component: Product,
    },
    {
        path: "/process",
        name: "process",
        component: Process,
    },
    {
        path: "/application",
        name: "application",
        component: Application,
    },
    {
        path: "/service",
        name: "service",
        component: Service,
    },
    {
        path: "/help",
        name: "help",
        component: Help,
    },
    {
        path: "/profile",
        name: "profile",
        component: Profile,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/:orgName?",
        name: "organizationPage",
        component: OrgInst,
        meta: {
            requiresAuth: true,
            requiresOrg: true,
            requiresStek: true,
            layout: "ApplicationLayout",
        },
        children: [
            {
                path: "stek/create",
                name: "createStek",
                component: CreateStek,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                },
            },
            {
                path: "user",
                name: "userManagement",
                component: UserManagement,
                meta: {
                    requiresStek: false,
                    requiresAuth: true,
                    requiresOrg: true,
                },
            },
            {
                path: "steks",
                component: undefined,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: true,
                },
                children: [
                    {
                        path: ":stekName",
                        component: undefined,
                        children: [
                            {
                                path: "live",
                                component: Live,
                                meta: {
                                    mode: "live",
                                },
                                children: [
                                    {
                                        path: "",
                                        name: "dashboard",
                                        component: Dashboard,
                                    },
                                ],
                            },
                            {
                                path: "",
                                alias: "biz/domain",
                                name: "stekPage",
                                component: StekPage,
                            },
                            {
                                path: "domain/:domainId",
                                name: "domainPage",
                                component: DomainPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain/:domainId/document/:artefactId?",
                                name: "domainDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "product/:productId",
                                name: "productPage",
                                component: ProductPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "role/:roleId",
                                name: "rolePage",
                                component: RolePage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "business_entity/:businessEntityId",
                                name: "businessEntityPage",
                                component: BusinessEntityPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "product/:productId/document/:artefactId?",
                                name: "productDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "process/:processId",
                                name: "processPage",
                                component: ProcessPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/process-definition/definition/:version?",
                                name: "processDefinitionPage",
                                component: ProcessDefinitionPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/document/:artefactId?",
                                name: "processDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "process/:processId/task/:taskId",
                                name: "taskPage",
                                component: RedirectByIdPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/task/:taskId/document/:artefactId?",
                                name: "taskDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: ":elementType/:elementId",
                                name: "processElementPage",
                                component: ProcessElementPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "document/:artefactId/:version?",
                                name: "documentPage",
                                component: DocumentPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "biz/domain/:domainName",
                                name: "domainDashboard",
                                component: RedirectByNamePage,
                                children: [
                                    {
                                        path: "",
                                        name: "domainOverview",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "roles",
                                        name: "roles",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "entities",
                                        name: "entities",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "files/:filePath?",
                                        name: "domainFiles",
                                        component: RedirectByNamePage,
                                    },
                                ],
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/:filePath?",
                                name: "productInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/:filePath?",
                                name: "processInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/tasks/:taskName/:filePath?",
                                name: "taskInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                name: "not-found",
                                path: "not-found",
                                component: NotFoundComponent,
                            },
                            {
                                path: ":pathMatch(.*)*",
                                component: NotFoundComponent,
                            },
                        ],
                    },
                    {
                        path: ":pathMatch(.*)*",
                        component: NotFoundComponent,
                    },
                ],
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        component: NotFoundComponent,
    },
];

const devRoute: RouteRecordRaw = {
    path: "/dev",
    name: "dev",
    component: Dev,
    meta: {
        requiresAuth: false,
        layout: "DevLayout",
    },
    children: [
        {
            path: "autologin",
            name: "autologin",
            component: AutoLogin,
            meta: {
                requiresAuth: false,
            },
        },
        {
            path: "code-preview",
            name: "code-preview",
            component: CodePreviewDemo,
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
    ],
};

const routes = [...baseRoutes];
if (import.meta.env.MODE !== "production") {
    routes.push(devRoute);
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        window.scrollTo(0, 0);
    },
});

export let routerIsReady = false;
// Make sure we can do something right after the router is ready.
router.isReady().then(async () => {
    const { authStatus } = useAuthenticationManager();
    const { isInitialized } = useAuthenticationStore();
    routerIsReady = true;

    const requiresAuth = router.currentRoute.value.meta.requiresAuth;
    const currentRouteName = String(router.currentRoute.value.name);

    // Sometimes the authentication library is initialized before the router is,
    // in those cases we still need to redirect to the right pages.
    await isInitialized;
    switch (authStatus.value) {
        case AuthStatus.SIGNED_IN: {
            if (
                ["login", "signup", "forgotPassword"].includes(currentRouteName)
            ) {
                router.push("/");
            }
            break;
        }
        case AuthStatus.MFA_REQUIRED:
        case AuthStatus.MFA_ENROLLMENT_REQUIRED:
        case AuthStatus.EMAIL_VERIFICATION_REQUIRED: {
            if (currentRouteName !== "login") {
                router.push({
                    name: "login",
                    query: {
                        returnTo: encodeURIComponent(
                            JSON.stringify({
                                name: router.currentRoute.value.name,
                                params: router.currentRoute.value.params,
                                query: router.currentRoute.value.query,
                            })
                        ),
                    },
                });
            }
            break;
        }
        case AuthStatus.NOT_SIGNED_IN: {
            // Special case for root path: redirect to /home instead of /login
            if (router.currentRoute.value.fullPath === "/") {
                router.push("/home");
            } else if (requiresAuth) {
                router.push({
                    name: "login",
                    query: {
                        returnTo: encodeURIComponent(
                            JSON.stringify({
                                name: router.currentRoute.value.name,
                                params: router.currentRoute.value.params,
                                query: router.currentRoute.value.query,
                            })
                        ),
                    },
                });
            }
            break;
        }
    }
});

router.beforeEach(async (guard) => {
    mixpanel.track_pageview({
        "Page Name": guard.name,
        "Page URL": guard.path,
        ...guard.params,
    });

    const { isInitialized } = useAuthenticationStore();
    // There is a brief period in which the authentication library is still
    // initializing. In this period the authentication status is unknown.
    // As soon as the authentication status is known it will be updated in
    // the store, so we utilize a short-lived watcher so that we can wait
    // for the authentication status to be updated in the router guard.
    await isInitialized;

    // Special case for root path when not authenticated
    const { authStatus } = useAuthenticationManager();
    if (
        guard.fullPath === "/" &&
        authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        return { path: "/home" };
    }
});

export default router;
